import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  message,
  Select,
  Form,
  Button,
  Alert,
  DatePicker,
  Row,
  Col,
  Segmented,
  Space,
} from "antd";
import type { TableProps } from "antd";
import { useGlobal } from "../contexts/GlobalContext";
import { useNavigate } from "react-router-dom";
import request from "../utils/request";
import ActionTypeTag from "../componets/TokenUsage/ActionTypeTag";
import WidePageHint from "../componets/common/WidePageHint";
import "remixicon/fonts/remixicon.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface TokenUsageItem {
  id: number;
  project_id: number;
  action: string;
  token_used: number;
  token_before: number;
  token_after: number;
  multiplier: number;
  content_size: number;
  summary_id?: number;
  agent_id?: number;
  model_name?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  created_at: string;
  project_name: string;
}

interface AgentOption {
  id: number;
  name: string;
  project_id?: number;
}

interface PublicModel {
  modelName: string;
  displayName?: string;
}

interface TokenUsageResponse {
  total: number;
  list: TokenUsageItem[];
}

interface TokenRunMetrics {
  totalRuns: number;
  completedRuns: number;
  cacheHits: number;
  cacheHitRate: number;
  rateRejects: number;
  budgetRejects: number;
  errorRuns: number;
}

const emptyMetrics: TokenRunMetrics = {
  totalRuns: 0,
  completedRuns: 0,
  cacheHits: 0,
  cacheHitRate: 0,
  rateRejects: 0,
  budgetRejects: 0,
  errorRuns: 0,
};

type ViewMode = "detail" | "byAction" | "byModel" | "byDay";

interface GroupRow {
  key: string;
  label: string;
  count: number;
  tokenUsed: number;
  promptTokens: number;
  completionTokens: number;
}

const actionLabelMap: Record<string, string> = {
  summary: "摘要生成",
  chat: "普通对话",
  chat_kb_mcp: "MCP对话",
  chat_kb: "知识库对话",
  hotword: "热词生成",
  search: "向量搜索",
  recharge: "Token充值",
  recommend: "AI推荐",
  agent_chat: "智能体对话",
  agent_chat_stream: "智能体流式对话",
  agent_chat_completion: "OpenAI兼容对话",
  agent_chat_completion_stream: "OpenAI兼容流式对话",
  agent_workflow: "智能体工作流",
};

// EXPORT_PAGE_SIZE/EXPORT_MAX_ROWS:导出/分组时分页拉全量的单页与上限(避免一次性全量拖垮接口)。
const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_ROWS = 5000;

const csvCell = (value: unknown): string => {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const buildGroups = (list: TokenUsageItem[], mode: ViewMode): GroupRow[] => {
  const map = new Map<string, GroupRow>();
  for (const item of list) {
    let key: string;
    let label: string;
    if (mode === "byModel") {
      key = item.model_name || "(无模型)";
      label = key;
    } else if (mode === "byDay") {
      key = (item.created_at || "").slice(0, 10) || "(未知)";
      label = key;
    } else {
      key = item.action || "(未知)";
      label = actionLabelMap[item.action] || item.action || "(未知)";
    }
    const row = map.get(key) || { key, label, count: 0, tokenUsed: 0, promptTokens: 0, completionTokens: 0 };
    row.count += 1;
    row.tokenUsed += Math.abs(item.token_used || 0);
    row.promptTokens += item.prompt_tokens || 0;
    row.completionTokens += item.completion_tokens || 0;
    map.set(key, row);
  }
  return Array.from(map.values()).sort((a, b) => b.tokenUsed - a.tokenUsed);
};

const TokenUsage: React.FC = () => {
  const { token, API_BASE_URL, projects, user } = useGlobal();
  const navigate = useNavigate();
  const [usageList, setUsageList] = useState<TokenUsageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [models, setModels] = useState<PublicModel[]>([]);
  const [metrics, setMetrics] = useState<TokenRunMetrics>(emptyMetrics);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("detail");
  const [groupRows, setGroupRows] = useState<GroupRow[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const paginationRef = React.useRef(pagination);
  const tokenBalance = user?.apiInfo?.postchat?.token || 0;
  const vipInfo = user?.apiInfo?.postchat?.vip_info;
  const currentPlan = vipInfo?.is_vip
    ? vipInfo.level === 3
      ? "Business"
      : vipInfo.level === 2
      ? "Pro Max"
      : "Pro"
    : "Free";

  // 更新 ref 当 pagination 改变时
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const buildFilterParams = useCallback(() => {
    const values = form.getFieldsValue();
    const requestParams: any = {};

    if (values.action) requestParams.action = values.action;
    if (values.project_id) requestParams.project_id = values.project_id;
    if (values.agent_id) requestParams.agent_id = values.agent_id;
    if (values.model_name) requestParams.model_name = values.model_name;
    if (values.summary_id) requestParams.summary_id = values.summary_id;

    if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
      requestParams.start_time = values.dateRange[0].format("YYYY-MM-DD");
      requestParams.end_time = values.dateRange[1].format("YYYY-MM-DD");
    }

    return requestParams;
  }, [form]);

  const fetchRunMetrics = useCallback(async () => {
    if (!token) return;

    setMetricsLoading(true);
    try {
      const response = await request.get<{
        code: number;
        msg: string;
        data: TokenRunMetrics;
      }>(`${API_BASE_URL}/api/v3/user/token-logs/metrics`, {
        params: buildFilterParams(),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setMetrics({ ...emptyMetrics, ...(response.data.data || {}) });
      } else {
        message.error(response.data.msg || "获取运行指标失败");
      }
    } catch (error) {
      console.error("获取运行指标失败", error);
    } finally {
      setMetricsLoading(false);
    }
  }, [API_BASE_URL, buildFilterParams, token]);

  const fetchTokenUsage = useCallback(
    async (params?: { current?: number; pageSize?: number }) => {
      if (!token) return;

      setLoading(true);
      try {
        const requestParams: any = {
          page: params?.current || paginationRef.current.current,
          page_size: params?.pageSize || paginationRef.current.pageSize,
          ...buildFilterParams(),
        };

        const response = await request.get<{
          code: number;
          msg: string;
          data: TokenUsageResponse;
        }>(`${API_BASE_URL}/api/v3/user/token-logs`, {
          params: requestParams,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code === 200) {
          setUsageList(response.data.data.list);
          setPagination((prev) => ({
            ...prev,
            current: params?.current || prev.current,
            pageSize: params?.pageSize || prev.pageSize,
            total: response.data.data.total,
          }));
        } else {
          message.error(response.data.msg || "获取Token消耗记录失败");
        }
      } catch (error) {
        console.error("获取Token消耗记录失败", error);
        message.error("获取Token消耗记录失败，请稍后重试");
      } finally {
        setLoading(false);
      }
    },
    [token, API_BASE_URL, buildFilterParams]
  );

  // fetchAllFiltered 按当前筛选条件分页拉取至上限,供导出与分组聚合使用。
  const fetchAllFiltered = useCallback(async (): Promise<{ list: TokenUsageItem[]; truncated: boolean }> => {
    const all: TokenUsageItem[] = [];
    let page = 1;
    let truncated = false;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await request.get<{ code: number; msg: string; data: TokenUsageResponse }>(
        `${API_BASE_URL}/api/v3/user/token-logs`,
        {
          params: { page, page_size: EXPORT_PAGE_SIZE, ...buildFilterParams() },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.code !== 200) {
        throw new Error(response.data.msg || "拉取记录失败");
      }
      const batch = response.data.data.list || [];
      all.push(...batch);
      const total = response.data.data.total || 0;
      if (all.length >= EXPORT_MAX_ROWS) {
        truncated = all.length < total;
        return { list: all.slice(0, EXPORT_MAX_ROWS), truncated };
      }
      if (batch.length < EXPORT_PAGE_SIZE || all.length >= total) {
        return { list: all, truncated };
      }
      page += 1;
    }
  }, [API_BASE_URL, buildFilterParams, token]);

  const handleExportCSV = useCallback(async () => {
    if (!token) return;
    setExporting(true);
    try {
      const { list, truncated } = await fetchAllFiltered();
      if (list.length === 0) {
        message.info("当前筛选条件下没有可导出的记录");
        return;
      }
      const headers = [
        "时间", "项目", "操作类型", "智能体ID", "模型",
        "Token消耗", "消费前余额", "消费后余额", "输入Token", "输出Token", "倍率", "摘要ID",
      ];
      const lines = [headers.map(csvCell).join(",")];
      for (const item of list) {
        lines.push([
          new Date(item.created_at).toLocaleString("zh-CN"),
          item.project_name || "",
          actionLabelMap[item.action] || item.action,
          item.agent_id || "",
          item.model_name || "",
          item.token_used,
          item.token_before,
          item.token_after,
          item.prompt_tokens || 0,
          item.completion_tokens || 0,
          item.multiplier,
          item.summary_id || "",
        ].map(csvCell).join(","));
      }
      const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `token-usage-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (truncated) {
        message.warning(`记录较多,仅导出前 ${EXPORT_MAX_ROWS} 条,请缩小时间范围后再导出`);
      } else {
        message.success(`已导出 ${list.length} 条记录`);
      }
    } catch (error) {
      console.error("导出失败", error);
      message.error("导出失败,请稍后重试");
    } finally {
      setExporting(false);
    }
  }, [fetchAllFiltered, token]);

  const fetchAgents = useCallback(
    async (projectId?: number) => {
      if (!token) return;
      try {
        const response = await request.get(`${API_BASE_URL}/api/v3/agents`, {
          params: {
            page: 1,
            pageSize: 50,
            project_id: projectId,
          },
        });
        if (response.data.code === 200) {
          setAgents(response.data.data.items || []);
        }
      } catch (error) {
        console.error("获取智能体列表失败", error);
      }
    },
    [API_BASE_URL, token]
  );

  useEffect(() => {
    if (!token) return;
    request
      .get(`${API_BASE_URL}/api/v3/models`)
      .then((response) => {
        if (response.data.code === 200) {
          setModels(response.data.data || []);
        }
      })
      .catch((error) => {
        console.error("获取模型列表失败", error);
      });
    fetchAgents();
  }, [API_BASE_URL, fetchAgents, token]);

  useEffect(() => {
    fetchTokenUsage();
    fetchRunMetrics();
  }, [fetchTokenUsage, fetchRunMetrics]);

  const loadGroups = useCallback(async () => {
    if (!token || viewMode === "detail") return;
    setGroupLoading(true);
    try {
      const { list, truncated } = await fetchAllFiltered();
      setGroupRows(buildGroups(list, viewMode));
      if (truncated) message.warning(`记录较多,分组仅统计前 ${EXPORT_MAX_ROWS} 条`);
    } catch (error) {
      console.error("分组统计失败", error);
      message.error("分组统计失败,请稍后重试");
    } finally {
      setGroupLoading(false);
    }
  }, [token, viewMode, fetchAllFiltered]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleTableChange = (paginationParams: any) => {
    fetchTokenUsage({
      current: paginationParams.current,
      pageSize: paginationParams.pageSize,
    });
  };

  // 处理搜索
  const handleSearch = () => {
    fetchTokenUsage({ current: 1 });
    fetchRunMetrics();
    loadGroups();
  };

  // 重置搜索条件
  const handleReset = () => {
    form.resetFields();
    fetchAgents();
    fetchTokenUsage({ current: 1 });
    fetchRunMetrics();
    loadGroups();
  };

  const metricPanels = [
    {
      label: "缓存命中率",
      value: metricsLoading ? "--" : `${metrics.cacheHitRate.toFixed(1)}%`,
      hint: `${metrics.cacheHits.toLocaleString()} 次命中`,
      icon: "ri-database-2-line",
      color: "#389e0d",
    },
    {
      label: "速率拒绝",
      value: metricsLoading ? "--" : metrics.rateRejects.toLocaleString(),
      hint: "限流拦截次数",
      icon: "ri-speed-up-line",
      color: "#d48806",
    },
    {
      label: "预算拒绝",
      value: metricsLoading ? "--" : metrics.budgetRejects.toLocaleString(),
      hint: "预算或额度拦截",
      icon: "ri-wallet-3-line",
      color: "#cf1322",
    },
    {
      label: "运行总数",
      value: metricsLoading ? "--" : metrics.totalRuns.toLocaleString(),
      hint: `${metrics.completedRuns.toLocaleString()} 次成功，${metrics.errorRuns.toLocaleString()} 次错误`,
      icon: "ri-pulse-line",
      color: "#1677ff",
    },
  ];

  const columns: TableProps<TokenUsageItem>["columns"] = [
    {
      title: "消费时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (time: string) => new Date(time).toLocaleString("zh-CN"),
    },
    {
      title: "项目名称",
      dataIndex: "project_name",
      key: "project_name",
      width: 150,
      render: (projectName: string) => projectName || "-",
    },
    {
      title: "操作类型",
      dataIndex: "action",
      key: "action",
      width: 120,
      render: (action: string) => <ActionTypeTag action={action} />,
    },
    {
      title: "智能体",
      dataIndex: "agent_id",
      key: "agent_id",
      width: 100,
      render: (agentId?: number) => {
        if (!agentId) return "-";
        const agent = agents.find((item) => item.id === agentId);
        return agent?.name || `#${agentId}`;
      },
    },
    {
      title: "模型",
      dataIndex: "model_name",
      key: "model_name",
      width: 150,
      render: (modelName?: string) => modelName || "-",
    },
    {
      title: "Token消耗",
      dataIndex: "token_used",
      key: "token_used",
      width: 120,
      render: (tokenUsed: number, record: TokenUsageItem) => {
        const isRecharge = record.action === "recharge";
        const displayValue = Math.abs(tokenUsed);

        if (isRecharge) {
          return (
            <span style={{ color: "#52c41a", fontWeight: "bold" }}>
              +{displayValue.toLocaleString()}
            </span>
          );
        } else {
          return (
            <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
              -{displayValue.toLocaleString()}
            </span>
          );
        }
      },
    },
    {
      title: "消费前余额",
      dataIndex: "token_before",
      key: "token_before",
      width: 120,
      render: (token: number) => token.toLocaleString(),
    },
    {
      title: "消费后余额",
      dataIndex: "token_after",
      key: "token_after",
      width: 120,
      render: (token: number) => token.toLocaleString(),
    },
    {
      title: "输入/输出",
      key: "token_breakdown",
      width: 100,
      render: (_value, record) =>
        record.prompt_tokens || record.completion_tokens
          ? `${record.prompt_tokens || 0}/${record.completion_tokens || 0}`
          : `${record.content_size || 0}`,
    },
    {
      title: "倍率",
      dataIndex: "multiplier",
      key: "multiplier",
      width: 80,
      render: (multiplier: number) => `${multiplier.toFixed(1)}x`,
    },
    {
      title: "摘要ID",
      dataIndex: "summary_id",
      key: "summary_id",
      width: 100,
      render: (id?: number) => id || "-",
    },
  ];

  const groupColumns: TableProps<GroupRow>["columns"] = [
    {
      title: viewMode === "byModel" ? "模型" : viewMode === "byDay" ? "日期" : "操作类型",
      dataIndex: "label",
      key: "label",
      render: (label: string, record) =>
        viewMode === "byAction" ? <ActionTypeTag action={record.key} /> : label,
    },
    { title: "记录数", dataIndex: "count", key: "count", width: 120, sorter: (a, b) => a.count - b.count, render: (v: number) => v.toLocaleString() },
    { title: "Token消耗", dataIndex: "tokenUsed", key: "tokenUsed", width: 140, defaultSortOrder: "descend", sorter: (a, b) => a.tokenUsed - b.tokenUsed, render: (v: number) => v.toLocaleString() },
    { title: "输入Token", dataIndex: "promptTokens", key: "promptTokens", width: 130, render: (v: number) => v.toLocaleString() },
    { title: "输出Token", dataIndex: "completionTokens", key: "completionTokens", width: 130, render: (v: number) => v.toLocaleString() },
  ];

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <WidePageHint message="消耗记录为宽表格,建议在桌面端查看完整列。" />
      <Card
        title={
          <span>
            <i
              className="ri-coin-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            />
            Token消耗记录
          </span>
        }
        variant="outlined"
        style={{ marginBottom: 16 }}
      >
        <Alert
          message="数据保留说明"
          description="Token消耗记录仅保留最近90天的数据！"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 16, background: "#fff" }}>
              <div style={{ color: "#6b7280", fontSize: 13 }}>Token余额</div>
              <div style={{ color: "#111827", fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>
                {tokenBalance.toLocaleString()}
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 16, background: "#fff" }}>
              <div style={{ color: "#6b7280", fontSize: 13 }}>当前套餐</div>
              <div style={{ color: "#111827", fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>
                {currentPlan}
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <Space wrap style={{ height: "100%", alignItems: "center" }}>
              <Button type="primary" onClick={() => navigate("/payment-checkout?type=member")}>
                升级套餐
              </Button>
              <Button onClick={() => navigate("/payment-checkout?type=token")}>
                加购 Token
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {metricPanels.map((item) => (
            <Col key={item.label} xs={24} sm={12} lg={6}>
              <div
                style={{
                  minHeight: 96,
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 16,
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    background: `${item.color}14`,
                    color: item.color,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    flex: "0 0 auto",
                  }}
                >
                  <i className={item.icon} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "#6b7280", fontSize: 13 }}>{item.label}</div>
                  <div style={{ color: "#111827", fontSize: 24, fontWeight: 700, lineHeight: 1.15 }}>
                    {item.value}
                  </div>
                  <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4, whiteSpace: "nowrap" }}>
                    {item.hint}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 24 }}
          onFinish={handleSearch}
        >
          <Form.Item name="project_id" label="项目">
            <Select
              style={{ width: 200 }}
              placeholder="选择项目"
              allowClear
              onChange={(value) => {
                form.setFieldValue("agent_id", undefined);
                fetchAgents(value);
              }}
            >
              {projects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="agent_id" label="智能体">
            <Select style={{ width: 180 }} placeholder="选择智能体" allowClear>
              {agents.map((agent) => (
                <Option key={agent.id} value={agent.id}>
                  {agent.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="model_name" label="模型">
            <Select style={{ width: 200 }} placeholder="选择模型" allowClear showSearch>
              {models.map((model) => (
                <Option key={model.modelName} value={model.modelName}>
                  {model.displayName
                    ? `${model.displayName} (${model.modelName})`
                    : model.modelName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="action" label="操作类型">
            <Select
              style={{ width: 150 }}
              placeholder="选择操作类型"
              allowClear
            >
              <Option value="summary">摘要生成</Option>
              <Option value="chat">普通对话</Option>
              <Option value="chat_kb_mcp">MCP对话</Option>
              <Option value="chat_kb">知识库对话</Option>
              <Option value="hotword">热词生成</Option>
              <Option value="search">向量搜索</Option>
              <Option value="recharge">Token充值</Option>
              <Option value="recommend">AI推荐</Option>
              <Option value="agent_chat">智能体对话</Option>
              <Option value="agent_chat_stream">智能体流式对话</Option>
              <Option value="agent_chat_completion">OpenAI兼容对话</Option>
              <Option value="agent_chat_completion_stream">OpenAI兼容流式对话</Option>
              <Option value="agent_workflow">智能体工作流</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="时间范围">
            <RangePicker
              style={{ width: 240 }}
              placeholder={["开始时间", "结束时间"]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              查询
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              重置
            </Button>
          </Form.Item>
        </Form>

        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Segmented
              value={viewMode}
              onChange={(value) => setViewMode(value as ViewMode)}
              options={[
                { label: "明细", value: "detail" },
                { label: "按操作类型", value: "byAction" },
                { label: "按模型", value: "byModel" },
                { label: "按日", value: "byDay" },
              ]}
            />
          </Col>
          <Col>
            <Space>
              <Button icon={<i className="ri-download-2-line" />} loading={exporting} onClick={handleExportCSV}>
                导出 CSV
              </Button>
            </Space>
          </Col>
        </Row>

        {viewMode === "detail" ? (
          <Table<TokenUsageItem>
            columns={columns}
            dataSource={usageList}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        ) : (
          <Table<GroupRow>
            columns={groupColumns}
            dataSource={groupRows}
            rowKey="key"
            loading={groupLoading}
            pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 组` }}
          />
        )}
      </Card>
    </div>
  );
};

export default TokenUsage;
